import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText, Badge, FormGroup } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiHash, FiUser, FiPackage, FiPercent } from "react-icons/fi";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const EditPurchase = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#2e4a3d' : '#dee2e6',
            boxShadow: state.isFocused ? '0 0 0 0.1rem rgba(46, 74, 61, 0.25)' : 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const [farmers, setFarmers] = useState([]);
    const [products, setProducts] = useState([]);
    const [formData, setFormData] = useState({
        purchase_no: "",
        purchase_date: dayjs().format('YYYY-MM-DD'),
        farmer_id: null, 
        farmer_type: "farmer", 
        product_id: null,
        rate: "",
        bag_weight: "0",
        gst_percentage: "0", 
    });
    const [sacks, setSacks] = useState([]);
    const [initialPaid, setInitialPaid] = useState(0);

    useEffect(() => {
        const loadInitialData = async () => {
            dispatch(loadingTrue());
            try {
                const [farmerRes, productRes, purchaseRes] = await Promise.all([
                    Handler({ method: "GET", url: "/farmer/list.php?limit=0" }),
                    Handler({ method: "GET", url: "/product/list.php" }),
                    Handler({ method: "GET", url: `/purchase/view.php?id=${id}` })
                ]);

                // 🔥 FIXED: Matching the exact structure from your Add page (f.contact)
                const farmerOptions = farmerRes?.success ? farmerRes.data?.map(f => ({
                    value: Number(f.id),
                    label: `${(f.contact?.name || "").toUpperCase()} (${f.contact?.phone || ""})`.trim(),
                    type: f.contact?.type ? String(f.contact.type).toLowerCase() : 'farmer'
                })) : [];

                const productOptions = productRes?.success ? productRes.data?.map(p => ({
                    value: Number(p.id),
                    label: (p.name || "").toUpperCase()
                })) : [];

                setFarmers(farmerOptions);
                setProducts(productOptions);

                if (purchaseRes?.success) {
                    const resData = purchaseRes.data;
                    const p = resData.purchase || resData; 
                    const s = resData.summary || resData.pricing || {};
                    const qty = resData.quantity || {};

                    const targetFarmerId = Number(resData.farmer?.id || p.farmer_id);
                    const targetProductId = Number(resData.product?.id || p.product_id);

                    const selectedFarmer = farmerOptions.find(f => f.value === targetFarmerId);
                    const selectedProduct = productOptions.find(pr => pr.value === targetProductId);

                    // Read API type safely from view response
                    const apiFarmerType = resData.farmer?.type ? String(resData.farmer.type).toLowerCase() : 'farmer';

                    setFormData({
                        purchase_no: p.purchase_no || "",
                        purchase_date: p.purchase_date || dayjs().format('YYYY-MM-DD'),
                        farmer_id: selectedFarmer || null,
                        farmer_type: selectedFarmer?.type || apiFarmerType, 
                        product_id: selectedProduct || null,
                        rate: s.rate || "",
                        bag_weight: (parseFloat(qty.total_kg || s.total_weight || 0) -
                            parseFloat(qty.net_kg || s.net_weight || 0)).toFixed(2),
                        gst_percentage: s.gst_percentage || "0" 
                    });

                    if (resData.sacks) setSacks(resData.sacks.map(sack => sack.weight));
                    else setSacks([""]); 

                    const totalPaid = resData.payment_summary?.paid_amount || resData.payment_summary?.total_paid || 0;
                    setInitialPaid(parseFloat(totalPaid));

                } else {
                    toast.error("No records found!");
                    navigate("/purchases");
                }
            } catch (err) {
                toast.error("Failed to load initial data");
                console.error("Error:", err);
            } finally {
                dispatch(loadingFalse());
            }
        };
        loadInitialData();
    }, [id, dispatch, navigate]);

    const addSackField = () => setSacks([...sacks, ""]);
    const removeSackField = (index) => setSacks(sacks.filter((_, i) => i !== index));
    const handleSackChange = (index, value) => {
        const updatedSacks = [...sacks];
        updatedSacks[index] = value;
        setSacks(updatedSacks);
    };

    const isTrader = String(formData.farmer_type).toLowerCase() === 'trader';

    // 🔥 GST & Total Calculations (Synced with Add page logic)
    const totalWeight = sacks.reduce((sum, w) => sum + (parseFloat(w) || 0), 0);
    const netWeight = totalWeight - (parseFloat(formData.bag_weight) || 0);
    const baseTotalAmount = netWeight > 0 ? netWeight * (parseFloat(formData.rate) || 0) : 0;

    let gstAmount = 0;
    let finalTotalWithGst = baseTotalAmount;

    if (isTrader) {
        const gstPercent = parseFloat(formData.gst_percentage) || 0;
        gstAmount = (baseTotalAmount * gstPercent) / 100;
        finalTotalWithGst = baseTotalAmount + gstAmount;
    }

    const blockInvalidChar = (e) => ["-", "e", "E"].includes(e.key) && e.preventDefault();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasEmptySack = sacks.some(s => s === "" || s === null || isNaN(s));

        const payload = {
            purchase_id: Number(id),
            purchase_no: formData.purchase_no,
            purchase_date: formData.purchase_date,
            farmer_id: formData.farmer_id ? Number(formData.farmer_id.value) : null,
            product_id: formData.product_id ? Number(formData.product_id.value) : null,
            rate: parseFloat(formData.rate),
            bag_weight: parseFloat(formData.bag_weight) || 0,
            sacks: sacks.filter(s => s !== "").map(s => parseFloat(s)),
            gst_percentage: isTrader ? (parseFloat(formData.gst_percentage) || 0) : 0
        };

        if (!payload.purchase_no) return toast.error("Purchase number is required");
        if (!payload.farmer_id) return toast.error("Please select a vendor profile");
        if (!payload.product_id) return toast.error("Please select a product type");
        if (!payload.rate || payload.rate <= 0) return toast.error("Please enter a valid rate");
        if (hasEmptySack) return toast.error("Please enter weight for all sack fields!");
        if (payload.sacks.length === 0) return toast.error("At least one sack weight entry is required");

        if (initialPaid > finalTotalWithGst) {
            return Swal.fire(
                "Payment Conflict",
                `An amount of ₹${initialPaid} has already been paid. Updated total (₹${finalTotalWithGst.toFixed(2)}) cannot be less than paid amount.`,
                "error"
            );
        }

        dispatch(loadingTrue());
        try {
            const res = await Handler({
                method: "POST",
                url: "/purchase/updatenew.php", 
                data: payload
            });

            if (res?.success) {
                Swal.fire("Updated!", "Purchase record updated successfully.", "success");
                navigate("/purchases");
            } else {
                toast.error(res?.message || "Failed to update purchase record");
            }
        } catch (err) {
            Swal.fire("Error", "Unable to connect to the server.", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 p-md-4 bg-light min-vh-100">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold text-theme m-0">Edit Purchase Entry</h3>
                        <p className="text-muted small m-0">Purchase ID: <strong>#{id}</strong></p>
                    </div>
                    <Button color="white" className="shadow-sm border-0 rounded-pill px-4" onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h5 className="fw-bold mb-4 border-start border-4 border-success ps-3">Entry Metrics</h5>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="small fw-bold text-uppercase opacity-75">Purchase No *</Label>
                                                <InputGroup className="overflow-hidden shadow-sm" style={{ borderRadius: '12px' }}>
                                                    <InputGroupText className="bg-white border-end-0"><FiHash /></InputGroupText>
                                                    <Input
                                                        className="border-start-0 ps-0 fw-bold"
                                                        style={{ height: '50px' }}
                                                        placeholder="Ex: PUR-101"
                                                        value={formData.purchase_no}
                                                        onChange={(e) => setFormData({ ...formData, purchase_no: e.target.value })}
                                                    />
                                                </InputGroup>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup className="d-flex flex-column">
                                                <Label className="small fw-bold text-uppercase opacity-75">Purchase Date *</Label>
                                                <DatePicker
                                                    disableFuture
                                                    format="DD/MM/YYYY"
                                                    value={dayjs(formData.purchase_date)}
                                                    onChange={(newValue) => {
                                                        setFormData({ ...formData, purchase_date: newValue ? newValue.format('YYYY-MM-DD') : '' });
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            sx: {
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '12px',
                                                                    height: '50px',
                                                                    '& fieldset': { borderColor: '#dee2e6' },
                                                                    '&:hover fieldset': { borderColor: '#2e4a3d' },
                                                                },
                                                                '& .MuiInputBase-input': { fontWeight: 'bold' }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-4">
                                    <h5 className="fw-bold mb-4 border-start border-4 border-success ps-3">Vendor Account & Material Profile</h5>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Label className="small fw-bold text-uppercase opacity-75 m-0">Account Profile Target Holder *</Label>
                                                {formData.farmer_id && (
                                                    <Badge color={isTrader ? 'warning' : 'success'} className="text-uppercase px-2 py-1 rounded-pill">
                                                        Classification: {formData.farmer_type}
                                                    </Badge>
                                                )}
                                            </div>
                                            <Select
                                                options={farmers}
                                                value={formData.farmer_id}
                                                isClearable
                                                styles={customSelectStyles}
                                                onChange={(opt) => setFormData({ 
                                                    ...formData, 
                                                    farmer_id: opt, 
                                                    farmer_type: opt?.type ? String(opt.type).toLowerCase() : 'farmer' 
                                                })}
                                                placeholder="Identify profile ledger..."
                                            />
                                        </Col>
                                        <Col md={isTrader ? 4 : 6}>
                                            <Label className="small fw-bold text-uppercase opacity-75">Product Sourced Variety</Label>
                                            <Select
                                                options={products}
                                                value={formData.product_id}
                                                styles={customSelectStyles}
                                                onChange={(opt) => setFormData({ ...formData, product_id: opt })}
                                                placeholder="Choose structural variant..."
                                            />
                                        </Col>
                                        <Col md={isTrader ? 4 : 6}>
                                            <Label className="small fw-bold text-uppercase opacity-75">Unit Sourced Rate / KG</Label>
                                            <InputGroup size="lg">
                                                <InputGroupText className="bg-white border-end-0 text-success fw-bold">₹</InputGroupText>
                                                <Input
                                                    className="border-start-0 ps-0 fw-bold"
                                                    type="number" step="0.01"
                                                    onKeyDown={blockInvalidChar}
                                                    value={formData.rate}
                                                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                                    placeholder="0.00"
                                                />
                                            </InputGroup>
                                        </Col>

                                        {/* 🔥 GST Field dynamically renders securely based on Trader status */}
                                        {isTrader && (
                                            <Col md={4}>
                                                <Label className="small fw-bold text-uppercase opacity-75">Applicable GST Rate (%)</Label>
                                                <InputGroup size="lg">
                                                    <InputGroupText className="bg-white border-end-0 text-warning"><FiPercent /></InputGroupText>
                                                    <Input
                                                        className="border-start-0 ps-0 fw-bold"
                                                        type="number" step="0.1"
                                                        onKeyDown={blockInvalidChar}
                                                        value={formData.gst_percentage}
                                                        onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                                                        placeholder="Ex: 5, 12, 18"
                                                    />
                                                </InputGroup>
                                            </Col>
                                        )}
                                    </Row>
                                </CardBody>
                            </Card>

                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h5 className="fw-bold mb-0 border-start border-4 border-success ps-3">Raw Sacks Weight Breakdown</h5>
                                            <small className="text-muted ms-3">Log explicit metric configuration elements for mass tracking</small>
                                        </div>
                                        <Button color="success" size="sm" outline onClick={addSackField} className="rounded-pill px-3">
                                            <i className="bx bx-plus"></i> Add Sack Item
                                        </Button>
                                    </div>
                                    <div className="p-3 bg-light rounded-4 border border-dashed">
                                        <Row className="g-3">
                                            {sacks.map((sack, index) => (
                                                <Col xs={6} md={3} key={index}>
                                                    <InputGroup size="sm" className="shadow-sm rounded-3 overflow-hidden">
                                                        <InputGroupText className="bg-white border-end-0 text-muted small">{index + 1}</InputGroupText>
                                                        <Input
                                                            className="border-start-0 border-end-0 ps-0 fw-bold text-center"
                                                            type="number" step="0.01"
                                                            onKeyDown={blockInvalidChar}
                                                            value={sack}
                                                            onChange={(e) => handleSackChange(index, e.target.value)}
                                                            placeholder="0.00"
                                                        />
                                                        {sacks.length > 1 && (
                                                            <Button color="white" className="border-start shadow-none text-danger" onClick={() => removeSackField(index)}>
                                                                <i className="bx bx-trash-alt"></i>
                                                            </Button>
                                                        )}
                                                    </InputGroup>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-4 sticky-top overflow-hidden" style={{ top: '100px' }}>
                                <div className="bg-theme p-4 text-white text-center" style={{ backgroundColor: '#2e4a3d' }}>
                                    <h6 className="opacity-75 text-uppercase small fw-bold mb-1">Final Settlement Payable</h6>
                                    <h2 className="fw-bold mb-0 text-warning">₹ {finalTotalWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</h2>
                                </div>
                                <CardBody className="p-4">
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between mb-3 small">
                                            <span className="text-muted text-uppercase fw-bold x-small">Paid Amount:</span>
                                            <span className="fw-bold text-success">₹{initialPaid.toLocaleString('en-IN')}</span>
                                        </div>

                                        <hr className="my-2 opacity-10" />

                                        <div className="d-flex justify-content-between mb-2 small">
                                            <span className="text-muted">Gross Mass Weight:</span>
                                            <span className="fw-bold">{totalWeight.toFixed(2)} KG</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2 align-items-center">
                                            <span className="text-muted x-small fw-bold text-uppercase">Bag Tare Weight:</span>
                                            <div style={{ width: '90px' }}>
                                                <Input
                                                    type="number" step="0.01"
                                                    className="form-control-sm text-end fw-bold border-success"
                                                    value={formData.bag_weight}
                                                    onChange={(e) => setFormData({ ...formData, bag_weight: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="d-flex justify-content-between mb-3 small">
                                            <span className="text-muted">Net Chargeable Weight:</span>
                                            <span className="fw-bold text-dark">{netWeight > 0 ? netWeight.toFixed(2) : "0.00"} KG</span>
                                        </div>

                                        <hr className="my-2 opacity-10" />

                                        {/* 🔥 Integrated breakdown matching AddPurchase UI perfectly */}
                                        <div className="d-flex justify-content-between mb-2 small">
                                            <span className="text-muted">Base Taxable Amount:</span>
                                            <span className="fw-bold">₹ {baseTotalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                        </div>

                                        {isTrader && (
                                            <div className="d-flex justify-content-between mb-2 small">
                                                <span className="text-muted">GST Breakup Tax ({formData.gst_percentage || 0}%):</span>
                                                <span className="fw-bold text-danger">+ ₹ {gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        )}

                                        <hr className="my-3 opacity-10" />
                                        
                                        <div className="d-flex justify-content-between mb-1 fw-bold fs-5">
                                            <span className="text-dark">Total Consolidated:</span>
                                            <span className="text-success">₹ {finalTotalWithGst > 0 ? finalTotalWithGst.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : "0.00"}</span>
                                        </div>
                                        <div className="d-flex justify-content-between small mt-2">
                                            <span className="text-muted">Balance to Pay:</span>
                                            <span className="text-danger fw-bold">₹{(finalTotalWithGst - initialPaid).toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>

                                    <Button color="success" block className="py-3 fw-bold rounded-4 shadow-sm w-100 mb-2" type="submit" style={{ background: '#2e4a3d', border: 'none' }}>
                                        <i className="bx bx-save me-1"></i> UPDATE PURCHASE LEDGER
                                    </Button>
                                    <p className="text-center text-muted m-0" style={{ fontSize: '11px' }}>
                                        * Ledger balance will adjust automatically.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </LocalizationProvider>
    );
};

export default EditPurchase;